import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Building2, Plus, Trash2, Percent, Banknote } from 'lucide-react';
import type { StructuredPaymentTerms, CancellationTier } from '../../types';

interface PaymentTermsEditorProps {
  value: StructuredPaymentTerms;
  onChange: (value: StructuredPaymentTerms) => void;
}

const generateId = () => `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const PaymentTermsEditor: React.FC<PaymentTermsEditorProps> = ({ value, onChange }) => {
  const { t } = useTranslation();

  // Cancellation Tiers
  const addCancellationTier = () => {
    const newTier: CancellationTier = {
      id: generateId(),
      daysBeforeArrival: 30,
      refundPercentage: 50,
    };
    onChange({
      ...value,
      cancellationPolicy: [...value.cancellationPolicy, newTier].sort(
        (a, b) => b.daysBeforeArrival - a.daysBeforeArrival
      ),
    });
  };

  const updateCancellationTier = (id: string, updates: Partial<CancellationTier>) => {
    const newTiers = value.cancellationPolicy
      .map(t => (t.id === id ? { ...t, ...updates } : t))
      .sort((a, b) => b.daysBeforeArrival - a.daysBeforeArrival);
    onChange({ ...value, cancellationPolicy: newTiers });
  };

  const removeCancellationTier = (id: string) => {
    onChange({
      ...value,
      cancellationPolicy: value.cancellationPolicy.filter(t => t.id !== id),
    });
  };

  return (
    <div className="space-y-6">
      {/* Bankverbindung — fest, nicht editierbar */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            Bankverbindung
          </CardTitle>
          <CardDescription>Diese Angaben gelten für alle Unterkünfte</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
            <span className="text-muted-foreground">Inhaber</span>
            <span className="font-medium">Herren Yvo Tobias & Lucas Patrick & Dominik Michael Richner</span>
            <span className="text-muted-foreground">Konto</span>
            <span>Privatkonto CHF – Rte du Lac 37, Môtier</span>
            <span className="text-muted-foreground">IBAN</span>
            <span className="font-mono">CH85 0076 9434 1800 7200 2</span>
            <span className="text-muted-foreground">BIC</span>
            <span className="font-mono">BLKBCH22</span>
          </div>
        </CardContent>
      </Card>

      {/* Stornierungsbedingungen */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Percent className="h-4 w-4" />
                {t('payment.cancellationPolicy')}
              </CardTitle>
              <CardDescription>Stornierungsgebühr je nach Stornierungszeitpunkt</CardDescription>
            </div>
            <Button size="sm" onClick={addCancellationTier}>
              <Plus className="h-4 w-4 mr-2" />
              Stufe hinzufügen
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {value.cancellationPolicy.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              Noch keine Stufen definiert.
            </p>
          ) : (
            <div className="space-y-3">
              {value.cancellationPolicy.map((tier) => (
                <div
                  key={tier.id}
                  className="flex items-center gap-4 border rounded-lg p-3 bg-muted/30"
                >
                  <div className="flex-1 grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label className="text-xs">Tage vor Anreise</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="0"
                          value={tier.daysBeforeArrival}
                          onChange={(e) =>
                            updateCancellationTier(tier.id, {
                              daysBeforeArrival: parseInt(e.target.value) || 0,
                            })
                          }
                          className="w-24"
                        />
                        <span className="text-sm text-muted-foreground">Tage</span>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Stornierungsgebühr</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={tier.refundPercentage}
                          onChange={(e) =>
                            updateCancellationTier(tier.id, {
                              refundPercentage: parseInt(e.target.value) || 0,
                            })
                          }
                          className="w-24"
                        />
                        <span className="text-sm text-muted-foreground">%</span>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:text-destructive"
                    onClick={() => removeCancellationTier(tier.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Gebühren */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base flex items-center gap-2">
            <Banknote className="h-4 w-4" />
            {t('payment.feesAndCosts')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('payment.cleaningFee')}</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">CHF</span>
                <Input
                  type="number"
                  min="0"
                  value={value.cleaningFee || ''}
                  onChange={(e) =>
                    onChange({ ...value, cleaningFee: Math.round(Number(e.target.value) || 0) })
                  }
                  placeholder="0"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('payment.paymentDeadline')}</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="0"
                  value={value.paymentDeadlineDays || ''}
                  onChange={(e) =>
                    onChange({ ...value, paymentDeadlineDays: parseInt(e.target.value) || 0 })
                  }
                  placeholder="0"
                />
                <span className="text-sm text-muted-foreground">Tage</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentTermsEditor;
